import { formatDate } from "../../utils/formatters";

/**
 * Renders a standard Georgia legal filing caption for divorce proceedings
 * @param {jsPDF} doc - The jsPDF document object
 * @param {Object} caseDetails - Case information object
 * @param {string} caseDetails.courtName - Full court name (e.g., "SUPERIOR COURT OF FULTON COUNTY")
 * @param {string} caseDetails.jurisdictionState - State (e.g., "GA")
 * @param {string} caseDetails.petitioner.fullName - Full name of petitioner
 * @param {string} caseDetails.respondent.fullName - Full name of respondent
 * @param {string} caseDetails.caseNumber - Civil action case number
 * @param {string} caseDetails.marriageDate - Date of marriage (ISO string)
 * @param {string} documentTitle - Title of the document (e.g., "DOMESTIC RELATIONS FINANCIAL AFFIDAVIT")
 * @param {Object} options - Formatting options
 * @param {number} options.pageWidth - Page width in points
 * @param {number} options.margin - Left/right margin in points (typically 72pt = 1 inch)
 * @param {number} options.startY - Starting Y position (typically 72pt = 1 inch from top)
 * @returns {number} - Y position after caption for continuing content
 */
export const renderGeorgiaLegalCaption = (doc, caseDetails, documentTitle, options = {}) => {

    const caseInfo = {
        courtName: caseDetails?.courtName || "SUPERIOR COURT OF [COUNTY] COUNTY",
        county: caseDetails?.jurisdictionState || "[COUNTY]",
        petitionerName: caseDetails?.petitioner?.fullName || "[PETITIONER NAME]",
        respondentName: caseDetails?.respondent?.fullName || "[RESPONDENT NAME]",
        caseNumber: caseDetails?.caseNumber,
        marriageDate: caseDetails?.marriageDate
    };

    const {
        pageWidth = doc.internal.pageSize.getWidth(),
        margin = 72, // 1 inch standard legal margin
        startY = 72   // 1 inch from top
    } = options;

    let yPosition = startY;
    const centerX = pageWidth / 2;
    const leftMargin = margin;
    const rightMargin = pageWidth - margin;
    const contentWidth = rightMargin - leftMargin;

    // Set standard legal document formatting
    doc.setFont("Times", "normal"); // Times New Roman is standard for legal docs

    // 1. Court Name (centered, all caps, bold)
    doc.setFontSize(12);
    doc.setFont("Times", "bold");
    const courtHeader = `${caseInfo.courtName?.toUpperCase() || 'SUPERIOR COURT OF [COUNTY] COUNTY'}`;
    doc.text(courtHeader, centerX, yPosition, { align: 'center' });
    yPosition += 16;

    doc.text("STATE OF GEORGIA", centerX, yPosition, { align: 'center' });
    yPosition += 24; // Extra space after court name

    // 2. Horizontal rule (optional but common)
    doc.setLineWidth(0.5);
    doc.line(leftMargin, yPosition, rightMargin, yPosition);
    yPosition += 16;

    // 3. Case Caption Box Layout
    // Left side: Petitioner vs Respondent
    // Right side: Case Number

    doc.setFont("Times", "normal");
    doc.setFontSize(11);

    // Calculate the dividing line for case info (about 2/3 left, 1/3 right)
    const dividerX = leftMargin + (contentWidth * 0.65);

    // Left side - Case parties
    let leftY = yPosition;

    // Petitioner
    doc.text(`${caseInfo.petitionerName?.toUpperCase() || '[PETITIONER NAME]'},`, leftMargin, leftY);
    leftY += 14;

    // Petitioner designation
    doc.setFont("Times", "normal");
    doc.setFontSize(10);
    doc.text("Petitioner,", leftMargin + 20, leftY);
    leftY += 18;

    // "vs." or "v."
    doc.setFont("Times", "normal");
    doc.setFontSize(11);
    doc.text("vs.", leftMargin, leftY);
    leftY += 18;

    // Respondent
    doc.text(`${caseInfo.respondentName?.toUpperCase() || '[RESPONDENT NAME]'},`, leftMargin, leftY);
    leftY += 14;

    // Respondent designation
    doc.setFont("Times", "normal");
    doc.setFontSize(10);
    doc.text("Respondent.", leftMargin + 20, leftY);

    // Right side - Case number and designation
    let rightY = yPosition;
    doc.setFont("Times", "normal");
    doc.setFontSize(11);

    // Case number
    const caseNumberText = `Civil Action Case No.: ${caseInfo.caseNumber || '[CASE NUMBER]'}`;
    doc.text(caseNumberText, dividerX + 10, rightY);
    rightY += 18;

    // Additional case designations (common in GA family court)
    // doc.setFontSize(10);
    // doc.text("Jury Trial Demanded", dividerX + 10, rightY);
    // rightY += 12;
    // doc.text("(Family Violence Act)", dividerX + 10, rightY);

    // Use the lower of the two sides for continuing
    yPosition = Math.max(leftY, rightY) + 24;

    // 4. Horizontal rule to close caption box
    doc.setLineWidth(0.5);
    doc.line(leftMargin, yPosition, rightMargin, yPosition);
    yPosition += 20;

    // 5. Document Title (centered, bold, all caps)
    doc.setFont("Times", "bold");
    doc.setFontSize(14);
    doc.text(documentTitle.toUpperCase(), centerX, yPosition, { align: 'center' });
    yPosition += 24;

    // 6. Additional case context (if marriage date available)
    if (caseInfo.marriageDate) {
        doc.setFont("Times", "normal");
        doc.setFontSize(11);
        const marriageText = `Date of Marriage: ${formatDate(caseInfo.marriageDate)}`;
        doc.text(marriageText, centerX, yPosition, { align: 'center' });
        yPosition += 16;
    }

    // 7. Purpose statement
    doc.setFont("Times", "italic");
    doc.setFontSize(10);
    const purposeText = "Prepared for litigation purposes only";
    doc.text(purposeText, centerX, yPosition, { align: 'center' });
    yPosition += 20;

    // Add some spacing before main content
    yPosition += 12;

    return yPosition;
};

// Helper function for date formatting (if not already available)
// const formatDate = (date) => {
//     const month = (date.getMonth() + 1).toString().padStart(2, '0');
//     const day = date.getDate().toString().padStart(2, '0');
//     const year = date.getFullYear();
//     return `${month}/${day}/${year}`;
// };